import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import styles from './AutocompleteField.module.css';

const AutocompleteField = ({ id, label, value, onChange, options, isDisabled = false, placeholder = "Digite para buscar...", onClear }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Encontrar o label da opção selecionada
  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : '';

  // Filtrar opções baseado no termo de busca
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Verificar se clicou dentro do wrapper ou dentro do dropdown
      const clickedInWrapper = wrapperRef.current?.contains(event.target);
      const clickedInDropdown = dropdownRef.current?.contains(event.target);
      
      if (!clickedInWrapper && !clickedInDropdown) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    const handleScroll = (event) => {
      // Não fechar se o scroll for dentro do próprio dropdown
      if (dropdownRef.current?.contains(event.target)) {
        return;
      }
      
      if (isOpen) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  // Resetar termo de busca quando valor muda externamente
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  // Calcular posição do dropdown para fixed positioning
  useEffect(() => {
    if (!isOpen) return;
    
    let animationFrameId;
    let initialized = false;
    
    const updateDropdownPosition = () => {
      if (inputRef.current && dropdownRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        
        // Encontrar o elemento scrollable (container com overflow)
        let scrollTop = window.scrollY;
        let scrollLeft = window.scrollX;
        
        // Procurar por um elemento pai com overflow
        let element = inputRef.current;
        while (element) {
          const style = window.getComputedStyle(element);
          if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
            scrollTop = element.scrollTop;
            scrollLeft = element.scrollLeft;
            break;
          }
          element = element.parentElement;
        }
        
        const top = rect.bottom + scrollTop;
        const left = rect.left + scrollLeft;
        const width = rect.width;
        
        dropdownRef.current.style.top = `${top}px`;
        dropdownRef.current.style.left = `${left}px`;
        dropdownRef.current.style.width = `${width}px`;
        initialized = true;
      } else if (!initialized) {
        animationFrameId = requestAnimationFrame(updateDropdownPosition);
        return;
      }
      
      animationFrameId = requestAnimationFrame(updateDropdownPosition);
    };
    
    animationFrameId = requestAnimationFrame(updateDropdownPosition);
    
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [isOpen]);

  const handleInputChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleInputClick = () => {
    if (!isDisabled) {
      setIsOpen(true);
      setSearchTerm('');
    }
  };

  const handleOptionClick = (optionValue) => {
    onChange({ target: { value: optionValue } });
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (isDisabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleOptionClick(filteredOptions[highlightedIndex].value);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onClear?.() ?? onChange({ target: { value: '' } });
    setSearchTerm('');
    inputRef.current?.focus();
  };

  return (
    <div className={styles.formGroup} ref={wrapperRef}>
      <label htmlFor={id}>{label}</label>
      <div className={`${styles.autocompleteWrapper} ${isDisabled ? styles.disabled : ''}`}>
        <input
          ref={inputRef}
          id={id}
          type="text"
          className={styles.formControl}
          value={isOpen ? searchTerm : displayValue}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          placeholder={displayValue || placeholder}
          autoComplete="off"
        />
        {value && !isDisabled && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClear}
            aria-label="Limpar seleção"
          >
            ×
          </button>
        )}
        <span 
          className={styles.dropdownIcon}
          onClick={handleInputClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleInputClick();
            }
          }}
          role="button"
          tabIndex="0"
          aria-label="Abrir dropdown"
        >
          ▼
        </span>
      </div>
      
      {isOpen && !isDisabled && ReactDOM.createPortal(
        <ul 
          ref={dropdownRef}
          className={styles.optionsList}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <li
                key={option.value}
                className={`${styles.option} ${
                  option.value === value ? styles.selected : ''
                } ${index === highlightedIndex ? styles.highlighted : ''}`}
                onClick={() => handleOptionClick(option.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOptionClick(option.value);
                  }
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                role="option"
                tabIndex="-1"
                aria-selected={option.value === value}
              >
                {option.label}
              </li>
            ))
          ) : (
            <li className={styles.noOptions}>Nenhum resultado encontrado</li>
          )}
        </ul>,
        document.body
      )}
    </div>
  );
};

AutocompleteField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  isDisabled: PropTypes.bool,
  placeholder: PropTypes.string,
  onClear: PropTypes.func
};

export default AutocompleteField;
